import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Clock, ArrowRight } from 'lucide-react';
import { Pill } from '@/components/ui/native';

const ACTION_TONE = {
  update: 'blue',
  reassign: 'purple',
  stage_change: 'amber',
  delete: 'rose',
  reopen: 'emerald',
};

function fmt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/**
 * Drop-in History tab.
 * Props:
 *   collection: "leads" | "opportunities" | "contacts" | "companies"
 *   docId:      uuid
 */
export default function HistoryTab({ collection, docId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!docId || !collection) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await axios.get(`${API}/admin/audit`, {
          params: { doc_id: docId, collection_name: collection, limit: 100 },
        });
        if (!cancelled) setLogs(res.data?.logs || []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [collection, docId]);

  if (loading) return <div className="p-6 text-sm text-slate-400">Loading history…</div>;
  if (error) return <div className="p-6 text-sm text-rose-600 dark:text-rose-400">{error}</div>;
  if (logs.length === 0) {
    return (
      <div className="p-8 text-center text-slate-400" data-testid="history-tab-empty">
        <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p className="text-sm">No edits recorded yet.</p>
        <p className="text-xs mt-1">Future updates, stage changes, and reassignments will appear here automatically.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4" data-testid="history-tab">
      {logs.map((row) => {
        const beforeKeys = Object.keys(row.before || {});
        const afterKeys = Object.keys(row.after || {});
        const changedFields = Array.from(new Set([...beforeKeys, ...afterKeys]));
        return (
          <div key={row.id} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3" data-testid={`history-row-${row.id}`}>
            <div className="flex items-center gap-2 mb-2 text-sm">
              <Pill tone={ACTION_TONE[row.action] || 'slate'}>{row.action}</Pill>
              <span className="font-medium text-slate-800 dark:text-slate-200">{row.actor_name}</span>
              <span className="text-xs text-slate-400">({row.actor_role})</span>
              <span className="ml-auto text-xs text-slate-400">{fmt(row.ts)}</span>
            </div>
            {changedFields.length > 0 && (
              <div className="space-y-1 mt-2">
                {changedFields.slice(0, 5).map((k) => (
                  <div key={k} className="font-mono text-xs grid grid-cols-12 gap-2 items-center">
                    <span className="col-span-3 text-slate-500 truncate" title={k}>{k}</span>
                    <span className="col-span-4 text-rose-600 dark:text-rose-400 truncate" title={JSON.stringify(row.before?.[k])}>
                      {row.before?.[k] === undefined ? <em className="text-slate-300">—</em> : JSON.stringify(row.before[k]).slice(0, 50)}
                    </span>
                    <ArrowRight className="col-span-1 w-3 h-3 text-slate-400 mx-auto" />
                    <span className="col-span-4 text-emerald-600 dark:text-emerald-400 truncate" title={JSON.stringify(row.after?.[k])}>
                      {row.after?.[k] === undefined ? <em className="text-slate-300">—</em> : JSON.stringify(row.after[k]).slice(0, 50)}
                    </span>
                  </div>
                ))}
                {changedFields.length > 5 && (
                  <p className="text-xs text-slate-400 pt-1">+{changedFields.length - 5} more fields</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
